import {
  Badge,
  Button,
  ButtonGroup,
  CategoryColorTable,
  Dialog,
  DialogBody,
  DialogContent,
  DialogTrigger,
  IconButton,
  Input,
  Label,
  SkeletonSpinnerCentered,
  Switch,
  Text,
} from '@civilgrid/ui/components';
import { formatFileSize } from '@civilgrid/ui/utils';
import { MAX_CATEGORY_COLORS, MAX_CUSTOM_DATA_LABEL_LENGTH } from '@civilgrid/utils/src/shapefile/dbfCategories';
import { useFeatureFlagEnabled } from '@posthog/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import type React from 'react';
import { useRef, useState } from 'react';
import { useSnapshot } from 'valtio';
import {
  addShapefileCustomData,
  addWmtsCustomData,
  deleteCustomData,
  inspectWmtsCapabilities,
  isCustomDataReady,
  listProjectCustomData,
  type ProjectCustomData,
  setCustomDataCategories,
  setCustomDataVisibility,
  updateCustomDataLabel,
  validateShapefileFile,
} from '~/api/projectCustomData';
import { capturePosthogEvent, captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { Notify } from '~/components2/Notify';
import {
  CATEGORY_COLOR_SWATCHES,
  categoryDisplayName,
  colorForCateTitle,
  colorWithOverride,
  humanizeCateTitle,
} from '~/pages/projectDetails/map/customDataSymbology';
import rootStore from '~/store';
import { CustomDataSourceDownloadButton, CustomDataSourceFileRow } from './CustomDataSourceDownload';
import type { ShapefileCategory } from './shapefileCategories';

// Gates only the color picker and recolor dialog.
export const CUSTOM_DATA_COLORS_FLAG = 'project-enable-custom-data-colors';

// Gates only renaming an ingested layer.
export const CUSTOM_DATA_LABELS_FLAG = 'project-enable-custom-data-labels';

// Exported so the map layer renderer (CustomDataLayers) shares one cached fetch.
export function customDataQueryKey(projectId: string) {
  return ['project-custom-data', { projectId }] as const;
}

// Poll while any row is still ingesting so it flips to ready/failed without a manual refresh.
// Shared by the files-panel, map, and legend queries (one cache key) so the map/legend pick up
// processing→ready even when the files panel isn't mounted to drive the poll.
export function customDataRefetchInterval(query: { state: { data?: ProjectCustomData[] } }): number | false {
  return query.state.data?.some((row) => row.state === 'processing') ? 3000 : false;
}

// One-click presets; the picker's custom section allows any other color.
const CATEGORY_COLOR_GROUPS = [{ label: 'APWA 811-safe', colors: CATEGORY_COLOR_SWATCHES }];

// Pull a server-supplied admin-facing message (400 bodies carry { reason }).
function errorReason(e: unknown, fallback: string): string {
  if (isAxiosError(e)) {
    const reason = e.response?.data?.reason;
    if (typeof reason === 'string' && reason.length > 0) return reason;
  }
  return fallback;
}

// Drops the entry instead of merging it when the typed name leaves the category displaying the name
// it already displays.
function withNameOverride(
  names: Record<string, string>,
  category: Parameters<typeof categoryDisplayName>[0],
  name: string,
): Record<string, string> {
  // The field is controlled by the stored override and the API measures after trimming, so the cap
  // applies to the trimmed length here.
  const trimmedName = name.trim();
  const capped =
    trimmedName.length > MAX_CUSTOM_DATA_LABEL_LENGTH ? trimmedName.slice(0, MAX_CUSTOM_DATA_LABEL_LENGTH) : name;
  const trimmed = capped.trim();
  const clearedWithNothingStored = trimmed === '' && !category.label?.trim();
  const next = { ...names };
  if (trimmed === categoryDisplayName(category) || clearedWithNothingStored) delete next[category.value];
  else next[category.value] = capped;
  return next;
}

export default function CustomDataSection() {
  const { me } = useSnapshot(rootStore);
  const { projectId } = useParams({ strict: false }) as { projectId: string };

  // Admin-only. Render nothing otherwise.
  if (!me?.isAdmin || !projectId) return null;

  return <CustomDataSectionInner projectId={projectId} />;
}

function CustomDataSectionInner({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient();

  const { data: rows, isLoading } = useQuery({
    refetchOnWindowFocus: false,
    queryKey: customDataQueryKey(projectId),
    queryFn: () => listProjectCustomData(projectId),
    refetchInterval: customDataRefetchInterval,
  });

  const refetchList = () => queryClient.invalidateQueries({ queryKey: customDataQueryKey(projectId) });

  return (
    <div className="relative min-h-[8rem] min-w-0 overflow-hidden">
      <section className="flex items-center justify-between">
        <div className="font-semibold flex items-center gap-1.5">
          Custom Data
          <span className="text-xs font-normal text-grey-50" data-testid="custom-data-internal-tag">
            [INTERNAL]
          </span>
        </div>
        <UploadCustomDataDialog projectId={projectId} onAdded={refetchList} />
      </section>

      <div className="p-4 flex flex-col gap-2">
        {isLoading ? (
          <SkeletonSpinnerCentered />
        ) : !rows || rows.length === 0 ? (
          <Text className="text-sm text-grey-50" data-testid="custom-data-empty-state">
            No custom data layers yet.
          </Text>
        ) : (
          rows.map((row) => <CustomDataRow key={row.id} projectId={projectId} row={row} onChanged={refetchList} />)
        )}
      </div>
    </div>
  );
}

function CustomDataRow({
  projectId,
  row,
  onChanged,
}: {
  projectId: string;
  row: ProjectCustomData;
  onChanged: () => void;
}) {
  const visibilityMutation = useMutation({
    mutationFn: (visibleToCustomer: boolean) => setCustomDataVisibility(projectId, row.id, visibleToCustomer),
    onSuccess: onChanged,
    onError: () => Notify.error({ message: "Couldn't update visibility." }),
  });

  const isReady = isCustomDataReady(row.state);

  return (
    <div className="flex w-full flex-col gap-2 overflow-hidden rounded-[8px] border border-grey-30 p-3">
      <div className="flex min-w-0 items-center gap-2">
        <div className="min-w-0 flex-1">
          <Text className="block truncate text-sm font-medium text-primary" title={row.label}>
            {row.label}
          </Text>
          {(row.sourceUrl || row.wmtsLayerIdentifier) && (
            <Text
              className="line-clamp-1 break-all text-xs text-grey-50"
              title={[row.sourceUrl, row.wmtsLayerIdentifier].filter(Boolean).join(' · ')}
              data-testid={`custom-data-source-${row.id}`}
            >
              {/* Host stays origin-only (no path/query) for secret-safety; the layer identifier
                  distinguishes two uploads from the same provider. */}
              {[row.sourceUrl, row.wmtsLayerIdentifier].filter(Boolean).join(' · ')}
            </Text>
          )}
        </div>
        <span className="shrink-0">
          <Badge color="blue" data-testid={`custom-data-type-${row.id}`}>
            {row.type.toUpperCase()}
          </Badge>
        </span>
        <span className="shrink-0">
          <Badge
            color={row.state === 'ready' ? 'green' : row.state === 'failed' ? 'red' : 'grey'}
            data-testid={`custom-data-state-${row.id}`}
          >
            {row.state}
          </Badge>
        </span>
      </div>

      <div className="flex items-center gap-3">
        {!isReady && (
          <Text className="min-w-0 text-xs text-grey-50" data-testid={`custom-data-visibility-locked-${row.id}`}>
            {row.state === 'failed' ? "Failed layers can't be shown to customers" : 'Available once ready'}
          </Text>
        )}
        <div className="ml-auto flex shrink-0 items-center gap-3">
          <div className="flex items-center gap-2">
            <Label htmlFor={`custom-data-visibility-${row.id}`} className="text-xs text-grey-70">
              Visible to customer
            </Label>
            <Switch
              id={`custom-data-visibility-${row.id}`}
              checked={isReady ? row.visibleToCustomer : false}
              disabled={visibilityMutation.isPending || !isReady}
              onCheckedChange={(checked) => visibilityMutation.mutate(checked)}
              data-testid={`custom-data-visibility-${row.id}`}
            />
          </div>

          <EditCustomDataDialog projectId={projectId} row={row} onChanged={onChanged} />

          <CustomDataSourceDownloadButton projectId={projectId} row={row} />

          <DeleteCustomDataDialog projectId={projectId} row={row} onDeleted={onChanged} />
        </div>
      </div>
    </div>
  );
}

// Renaming and recoloring an ingested layer and its categories, each section gated on its own flag.
// Every category is sent with an explicit color, matching the upload path, so a later change to the
// derived palette can't restyle a layer already on the map.
export function EditCustomDataDialog({
  projectId,
  row,
  onChanged,
}: {
  projectId: string;
  row: ProjectCustomData;
  onChanged: () => void;
}) {
  // Holds only the swatches this admin changed; everything else reads through to the layer's
  // stored colors, so a reopen after a save can never show a color the map isn't using.
  const [colors, setColors] = useState<Record<string, string>>({});
  // Same read-through rule as `colors`, for the names this admin retyped.
  const [names, setNames] = useState<Record<string, string>>({});
  const [label, setLabel] = useState(row.label);
  const [open, setOpen] = useState(false);

  const canEditLabel = useFeatureFlagEnabled(CUSTOM_DATA_LABELS_FLAG) === true;
  // Ingest owns the category list, so there is nothing to edit until it has finished a shapefile.
  const hasCategories = isCustomDataReady(row.state) && row.type === 'shapefile' && row.categories.length > 0;
  const canEditColors = useFeatureFlagEnabled(CUSTOM_DATA_COLORS_FLAG) === true && hasCategories;
  const canRenameCategories = canEditLabel && hasCategories;

  // Own-property lookup: a category named `toString` would otherwise read a function off the prototype.
  const nameOverride = (value: string) => (Object.hasOwn(names, value) ? names[value] : undefined);

  const changeCategoryName = (value: string, name: string) => {
    const category = row.categories.find((c) => c.value === value);
    if (category) setNames((prev) => withNameOverride(prev, category, name));
  };

  // Only retyped names travel; the server leaves a category's stored name alone when none is sent.
  const pickedCategories = () =>
    row.categories.map((category) => {
      const name = nameOverride(category.value);
      return {
        value: category.value,
        color: colorWithOverride(category, colors),
        ...(name === undefined ? {} : { label: name.trim() }),
      };
    });

  const nextLabel = label.trim();
  // The label endpoint rejects the request while the flag is off, so a colors-only save sends none.
  const labelChanged = canEditLabel && nextLabel.length > 0 && nextLabel !== row.label;
  const colorsChanged = Object.keys(colors).length > 0;
  const namesChanged = Object.keys(names).length > 0;

  // The two requests land independently, so a refresh after either one keeps the list from showing
  // a label the server has already changed.
  const saveLabel = useMutation({
    mutationFn: () => updateCustomDataLabel(projectId, row.id, nextLabel),
    onSuccess: onChanged,
    onError: (e) => Notify.error({ message: errorReason(e, "Couldn't rename layer.") }),
  });

  const saveCategories = useMutation({
    mutationFn: () => setCustomDataCategories(projectId, row.id, pickedCategories()),
    onSuccess: onChanged,
    onError: (e) => Notify.error({ message: errorReason(e, "Couldn't update categories.") }),
  });

  // A label-only save must not PATCH categories: the endpoint rejects layers that aren't ready shapefiles.
  // Every open and close starts the form over from the stored layer.
  const handleOpenChange = (isOpen: boolean) => {
    setColors({});
    setNames({});
    setLabel(row.label);
    if (isOpen && canEditColors) {
      capturePosthogEvent(POSTHOG_EVENTS.customDataCategoryColorsOpened, {
        project_id: projectId,
        custom_data_id: row.id,
      });
    }
    setOpen(isOpen);
  };

  const save = async () => {
    const results = await Promise.allSettled([
      ...(labelChanged ? [saveLabel.mutateAsync()] : []),
      ...(colorsChanged || namesChanged ? [saveCategories.mutateAsync()] : []),
    ]);
    if (results.every((result) => result.status === 'fulfilled')) handleOpenChange(false);
  };

  if (!canEditLabel && !canEditColors) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <IconButton
          icon="edit"
          variant="secondary"
          aria-label="Edit layer"
          data-testid={`custom-data-edit-trigger-${row.id}`}
        />
      </DialogTrigger>
      <DialogContent
        title="Edit layer"
        className="z-[110] w-auto min-w-min max-w-[75vw] w-[560px]"
        overlayClassName="z-[100]"
        data-testid={`custom-data-edit-${row.id}-dialog`}
      >
        <DialogBody>
          <div className="flex flex-col gap-4 p-1">
            <CustomDataSourceFileRow projectId={projectId} row={row} />
            {canEditLabel && (
              <div className="flex flex-col gap-1">
                <Label htmlFor={`custom-data-edit-label-${row.id}`}>Display label</Label>
                <Input
                  id={`custom-data-edit-label-${row.id}`}
                  value={label}
                  maxLength={MAX_CUSTOM_DATA_LABEL_LENGTH}
                  onChange={(e) => setLabel(e.target.value)}
                  data-testid={`custom-data-edit-label-input-${row.id}`}
                />
              </div>
            )}
            {(canEditColors || canRenameCategories) && (
              <>
                <Text className="text-sm text-grey-70">
                  Sets how <span className="font-medium">{row.label}</span>'s categories appear on the map and in the
                  legend.
                </Text>
                <CategoryColorTable
                  rows={row.categories.map((category) => ({
                    value: category.value,
                    label: nameOverride(category.value) ?? categoryDisplayName(category),
                    count: category.count,
                    color: colorWithOverride(category, colors),
                  }))}
                  onColorChange={
                    canEditColors ? (value, color) => setColors((prev) => ({ ...prev, [value]: color })) : undefined
                  }
                  onNameChange={canRenameCategories ? changeCategoryName : undefined}
                  groups={CATEGORY_COLOR_GROUPS}
                  allowCustomColor
                  testIdPrefix={`custom-data-edit-category-${row.id}`}
                  data-testid={`custom-data-edit-colors-table-${row.id}`}
                />
              </>
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => handleOpenChange(false)}
                data-testid={`custom-data-edit-cancel-${row.id}`}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                loading={saveLabel.isPending || saveCategories.isPending}
                disabled={!labelChanged && !colorsChanged && !namesChanged}
                onClick={save}
                data-testid={`custom-data-edit-save-${row.id}`}
              >
                Save
              </Button>
            </div>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

function DeleteCustomDataDialog({
  projectId,
  row,
  onDeleted,
}: {
  projectId: string;
  row: ProjectCustomData;
  onDeleted: () => void;
}) {
  const deleteMutation = useMutation({
    mutationFn: () => deleteCustomData(projectId, row.id),
    // Removal is captured server-side (source: 'api'); don't double-fire here.
    onSuccess: onDeleted,
    onError: (e) => Notify.error({ message: errorReason(e, "Couldn't delete layer.") }),
  });
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="danger" size="standard" data-testid={`custom-data-delete-trigger-${row.id}`}>
          Delete
        </Button>
      </DialogTrigger>
      <DialogContent
        title="Delete custom data layer?"
        className="z-[110] w-auto min-w-min max-w-[75vw]"
        overlayClassName="z-[100]"
        data-testid={`custom-data-delete-${row.id}-dialog`}
      >
        <DialogBody>
          <div className="flex flex-col gap-4 p-1">
            <Text className="text-sm text-grey-70">
              This removes <span className="font-medium">{row.label}</span> from the project. This cannot be undone.
            </Text>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setOpen(false)}
                data-testid={`custom-data-delete-cancel-${row.id}`}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                loading={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate(undefined, { onSuccess: () => setOpen(false) })}
                data-testid={`custom-data-delete-confirm-${row.id}`}
              >
                Delete
              </Button>
            </div>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

type CustomDataTab = 'shapefile' | 'wmts';

// One upload surface hosting both custom-data sources (WMTS + shapefile), per CG-518. The two
// sources are split across tabs so only one source — and one primary CTA — shows at a time.
function UploadCustomDataDialog({ projectId, onAdded }: { projectId: string; onAdded: () => void }) {
  const [tab, setTab] = useState<CustomDataTab>('shapefile');
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (isOpen) {
          setTab('shapefile');
          capturePosthogEvent(POSTHOG_EVENTS.customDataUploadOpened, { project_id: projectId });
        }
        setOpen(isOpen);
      }}
    >
      <DialogTrigger asChild>
        <IconButton icon="upload" variant="primary" data-testid="custom-data-add-button">
          Upload Custom Data
        </IconButton>
      </DialogTrigger>
      <DialogContent
        title={
          <span className="flex items-center gap-1.5">
            Upload Custom Data
            <span className="text-xs font-normal text-grey-50" data-testid="custom-data-upload-internal-tag">
              [INTERNAL]
            </span>
          </span>
        }
        className="z-[110] w-auto min-w-min max-w-[75vw] w-[560px]"
        overlayClassName="z-[100]"
        data-testid="custom-data-upload-dialog"
      >
        <DialogBody>
          <div className="flex flex-col gap-4 p-1">
            <ButtonGroup className="w-full [&>*]:flex-1">
              <Button
                type="button"
                variant={tab === 'shapefile' ? 'primary' : 'secondary'}
                onClick={() => setTab('shapefile')}
                data-testid="custom-data-tab-shapefile"
              >
                Shapefile
              </Button>
              <Button
                type="button"
                variant={tab === 'wmts' ? 'primary' : 'secondary'}
                onClick={() => setTab('wmts')}
                data-testid="custom-data-tab-wmts"
              >
                WMTS
              </Button>
            </ButtonGroup>

            {tab === 'shapefile' ? (
              <AddShapefileForm projectId={projectId} onAdded={onAdded} close={close} />
            ) : (
              <AddWmtsForm projectId={projectId} onAdded={onAdded} close={close} />
            )}
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

export function AddShapefileForm({
  projectId,
  onAdded,
  close,
}: {
  projectId: string;
  onAdded: () => void;
  close: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [label, setLabel] = useState('');
  const [categories, setCategories] = useState<ShapefileCategory[]>([]);
  const [colors, setColors] = useState<Record<string, string>>({});
  // Holds only the categories this uploader retyped; everything else falls back to the humanized value.
  const [names, setNames] = useState<Record<string, string>>({});
  const [isReadingCategories, setIsReadingCategories] = useState(false);
  // Default ON — internal uploads are visible by default (matches the WMTS tab).
  const [visibleToCustomer, setVisibleToCustomer] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const colorsEnabled = useFeatureFlagEnabled(CUSTOM_DATA_COLORS_FLAG) === true;
  const labelsEnabled = useFeatureFlagEnabled(CUSTOM_DATA_LABELS_FLAG) === true;
  const inputRef = useRef<HTMLInputElement>(null);

  // Each pick gets a token; a read that resolves after a newer pick started is discarded, so a slow
  // zip cannot overwrite the categories of the file that replaced it.
  const readToken = useRef(0);

  const clearCategories = () => {
    readToken.current += 1;
    setCategories([]);
    setColors({});
    setNames({});
    setIsReadingCategories(false);
  };

  // Validate + stage a single file (drag-drop or browse share this path), then read its categories
  // so the uploader can color and name them; an unreadable zip just leaves the category section
  // hidden, while a chunk that won't load shows an error.
  const pickFile = async (next: File | undefined) => {
    if (!next) return;
    const validationError = validateShapefileFile(next);
    if (validationError) {
      setError(validationError);
      setFile(null);
      clearCategories();
      return;
    }
    setError(null);
    setFile(next);
    clearCategories();
    if (!colorsEnabled && !labelsEnabled) return;
    const token = readToken.current;
    setIsReadingCategories(true);
    try {
      // Loaded on demand: the DBF reader pulls in iconv-lite's encoding tables (~170KB gzipped) and
      // this dialog is admin-only, while CustomDataSection itself is on the map's static path.
      const categoryReader = await import('./shapefileCategories').catch(() => null);
      if (token !== readToken.current) return;
      if (!categoryReader) {
        setError("Couldn't load the category color picker. Refresh the page and try again.");
        return;
      }
      const found = await categoryReader.readShapefileCategories(next);
      if (token !== readToken.current) return;
      // The API rejects a list longer than this outright, so an over-cap zip uploads with
      // server-derived colors instead of failing.
      if (found.length > MAX_CATEGORY_COLORS) return;
      setCategories(found);
      setColors(Object.fromEntries(found.map(({ value }) => [value, colorForCateTitle(value)])));
    } finally {
      if (token === readToken.current) setIsReadingCategories(false);
    }
  };

  // Own-property lookup: a category named `toString` would otherwise read a function off the prototype.
  const nameOverride = (value: string) => (Object.hasOwn(names, value) ? names[value] : undefined);

  const changeCategoryName = (value: string, name: string) => {
    const category = categories.find((c) => c.value === value);
    if (category) setNames((prev) => withNameOverride(prev, category, name));
  };

  // Every category is sent with an explicit color, so a later palette change can't restyle a layer
  // that has already been uploaded; only retyped names travel.
  const pickedCategories = () =>
    categories.map(({ value, count }) => {
      const name = nameOverride(value);
      return {
        value,
        count,
        color: colors[value] ?? colorForCateTitle(value),
        ...(name === undefined ? {} : { label: name.trim() }),
      };
    });

  const changedColorCount = () =>
    categories.filter(({ value }) => colors[value] && colors[value] !== colorForCateTitle(value)).length;

  const renamedCount = () =>
    categories.filter(({ value }) => {
      const name = nameOverride(value);
      return name !== undefined && name.trim() !== humanizeCateTitle(value);
    }).length;

  const uploadMutation = useMutation({
    // The edit counts ride in the mutation variables so an edit made mid-flight can't make the
    // success event describe state the API never received.
    mutationFn: ({
      file,
      label,
    }: {
      file: File;
      label?: string;
      categoryCount: number;
      changedColorCount: number;
      renamedCount: number;
    }) => addShapefileCustomData(projectId, file, label, visibleToCustomer, pickedCategories()),
    onSuccess: (row, variables) => {
      captureTypedPosthogEvent(POSTHOG_EVENTS.customDataShapefileSubmitted, {
        project_id: projectId,
        custom_data_id: row.id,
      });
      // Only when the uploader actually recolored or renamed something; an untouched upload is
      // already covered by the submit event above.
      if (variables.changedColorCount > 0 || variables.renamedCount > 0) {
        captureTypedPosthogEvent(POSTHOG_EVENTS.customDataCategoryColorsSet, {
          project_id: projectId,
          custom_data_id: row.id,
          category_count: variables.categoryCount,
          changed_count: variables.changedColorCount,
          renamed_count: variables.renamedCount,
        });
      }
      // Reset so a re-opened dialog starts clean.
      setFile(null);
      setLabel('');
      clearCategories();
      setVisibleToCustomer(true);
      setError(null);
      onAdded();
      close();
    },
    onError: (e) => setError(errorReason(e, "Couldn't upload shapefile.")),
  });

  const onUpload = () => {
    setError(null);
    if (!file) {
      setError('Choose a .zip shapefile to upload.');
      return;
    }
    uploadMutation.mutate({
      file,
      label: label.trim() || undefined,
      categoryCount: categories.length,
      changedColorCount: changedColorCount(),
      renamedCount: renamedCount(),
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <Label>Shapefile bundle</Label>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            void pickFile(e.dataTransfer.files?.[0]);
          }}
          className={`flex w-full flex-col items-center justify-center gap-1 rounded-[8px] border border-dashed p-6 text-center text-sm transition-colors ${
            isDragging ? 'border-blue-50 bg-blue-10' : 'border-grey-30 text-grey-50 hover:border-grey-50'
          }`}
          data-testid="custom-data-shapefile-dropzone"
        >
          <span>Drag a zipped shapefile here, or browse</span>
          <span className="text-xs text-grey-50">.zip only, up to 50MB</span>
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".zip"
          className="hidden"
          onChange={(e) => {
            void pickFile(e.target.files?.[0]);
            // Reset so re-selecting the same file still fires onChange.
            e.target.value = '';
          }}
          data-testid="custom-data-shapefile-input"
        />
      </div>

      {file && (
        <div
          className="flex items-center justify-between gap-2 rounded-[8px] border border-grey-30 p-2"
          data-testid="custom-data-shapefile-selected"
        >
          <div className="min-w-0">
            <Text className="block truncate text-sm font-medium text-primary" title={file.name}>
              {file.name}
            </Text>
            <Text className="text-xs text-grey-50">{formatFileSize(file.size)}</Text>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="small"
            onClick={() => {
              setFile(null);
              setError(null);
              clearCategories();
            }}
            data-testid="custom-data-shapefile-remove"
          >
            Remove
          </Button>
        </div>
      )}

      {isReadingCategories && (
        <Text className="text-sm text-grey-50" data-testid="custom-data-category-colors-loading">
          Reading categories…
        </Text>
      )}

      {(colorsEnabled || labelsEnabled) && categories.length > 0 && (
        <div className="flex flex-col gap-1">
          <Label>{colorsEnabled ? 'Layer colors' : 'Layer categories'}</Label>
          <CategoryColorTable
            rows={categories.map(({ value, count }) => ({
              value,
              label: nameOverride(value) ?? humanizeCateTitle(value),
              count,
              color: colors[value] ?? colorForCateTitle(value),
            }))}
            onColorChange={
              colorsEnabled ? (value, color) => setColors((prev) => ({ ...prev, [value]: color })) : undefined
            }
            onNameChange={labelsEnabled ? changeCategoryName : undefined}
            groups={CATEGORY_COLOR_GROUPS}
            allowCustomColor
            countLabel="Records"
            testIdPrefix="custom-data-category"
            data-testid="custom-data-category-colors"
          />
        </div>
      )}

      <div className="flex flex-col gap-1">
        <Label htmlFor="custom-data-shapefile-label">Display label (optional)</Label>
        <Input
          id="custom-data-shapefile-label"
          value={label}
          maxLength={MAX_CUSTOM_DATA_LABEL_LENGTH}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Defaults to the file name"
          data-testid="custom-data-shapefile-label-input"
        />
      </div>

      <div className="flex items-center gap-2">
        <Switch
          id="custom-data-shapefile-visibility"
          checked={visibleToCustomer}
          onCheckedChange={setVisibleToCustomer}
          data-testid="custom-data-shapefile-visibility"
        />
        <Label htmlFor="custom-data-shapefile-visibility" className="text-sm">
          Make visible to customer
        </Label>
      </div>

      {error && (
        <Text className="text-sm text-red-50" data-testid="custom-data-shapefile-error">
          {error}
        </Text>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={close} data-testid="custom-data-shapefile-cancel">
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          loading={uploadMutation.isPending}
          // Blocked while the categories are still being read, so an upload can't ship without them.
          disabled={!file || isReadingCategories}
          onClick={onUpload}
          data-testid="custom-data-shapefile-upload-button"
        >
          Upload shapefile
        </Button>
      </div>
    </div>
  );
}

function AddWmtsForm({ projectId, onAdded, close }: { projectId: string; onAdded: () => void; close: () => void }) {
  const [capabilitiesUrl, setCapabilitiesUrl] = useState('');
  const [layerIdentifier, setLayerIdentifier] = useState('');
  const [label, setLabel] = useState('');
  const [visibleToCustomer, setVisibleToCustomer] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [lastInspectedUrl, setLastInspectedUrl] = useState('');

  const inspectMutation = useMutation({
    mutationFn: (url: string) => inspectWmtsCapabilities(projectId, url),
    onSuccess: (result, url) => {
      setLastInspectedUrl(url);
      setInfo(null);
      if (result.layers.length === 1) {
        const [only] = result.layers;
        setLayerIdentifier(only.identifier);
        setLabel(only.suggestedLabel);
      } else {
        setInfo(`Found ${result.layers.length} layers — enter the layer identifier you want.`);
      }
      capturePosthogEvent(POSTHOG_EVENTS.customDataWmtsInspected, {
        project_id: projectId,
        layer_count: result.layers.length,
        hostname: result.hostname,
      });
    },
    // Non-blocking: inspection failing shouldn't stop a manual submit.
    onError: (e) => setInfo(errorReason(e, "Couldn't read that capabilities URL — enter the fields manually.")),
  });
  const isInspecting = inspectMutation.isPending;

  const onCapabilitiesBlur = () => {
    const url = capabilitiesUrl.trim();
    // isInspecting guards a double-fire if the field is blurred again before the first inspect resolves.
    if (!url || url === lastInspectedUrl || isInspecting) return;
    try {
      const p = new URL(url);
      if (p.protocol !== 'http:' && p.protocol !== 'https:') return;
    } catch {
      return;
    }
    inspectMutation.mutate(url);
  };

  const addMutation = useMutation({
    mutationFn: () =>
      addWmtsCustomData(projectId, {
        capabilitiesUrl: capabilitiesUrl.trim(),
        layerIdentifier: layerIdentifier.trim() || undefined,
        label: label.trim() || undefined,
        visibleToCustomer,
      }),
    onSuccess: (row) => {
      capturePosthogEvent(POSTHOG_EVENTS.customDataWmtsSubmitted, {
        project_id: projectId,
        custom_data_id: row.id,
      });
      // Reset so a re-opened dialog starts clean (the dialog keeps this form mounted).
      setCapabilitiesUrl('');
      setLayerIdentifier('');
      setLabel('');
      setVisibleToCustomer(true);
      setError(null);
      setInfo(null);
      setLastInspectedUrl('');
      onAdded();
      close();
    },
    onError: (e) => setError(errorReason(e, "Couldn't add WMTS layer.")),
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const trimmedUrl = capabilitiesUrl.trim();
    if (!trimmedUrl) {
      setError('Capabilities URL is required.');
      return;
    }
    // Fast client-side shape check; the server stays the source of truth.
    try {
      const parsed = new URL(trimmedUrl);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        setError('Capabilities URL must start with http:// or https://.');
        return;
      }
    } catch {
      setError('Please enter a valid Capabilities URL.');
      return;
    }
    addMutation.mutate();
  };

  return (
    <form className="flex flex-col gap-4 p-1" onSubmit={onSubmit}>
      <div className="flex flex-col gap-1">
        <Label htmlFor="custom-data-capabilities-url">Capabilities URL</Label>
        <Input
          id="custom-data-capabilities-url"
          value={capabilitiesUrl}
          onChange={(e) => setCapabilitiesUrl(e.target.value)}
          onBlur={onCapabilitiesBlur}
          placeholder="https://.../WMTSCapabilities.xml"
          data-testid="custom-data-capabilities-url-input"
        />
        {(isInspecting || info) && (
          <Text className="text-sm text-grey-60" data-testid="custom-data-inspect-status">
            {isInspecting ? 'Reading capabilities…' : info}
          </Text>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="custom-data-layer-identifier">Layer identifier (optional)</Label>
        <Input
          id="custom-data-layer-identifier"
          value={layerIdentifier}
          onChange={(e) => setLayerIdentifier(e.target.value)}
          disabled={isInspecting}
          placeholder={isInspecting ? 'Reading capabilities…' : undefined}
          data-testid="custom-data-layer-identifier-input"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="custom-data-label">Display label (optional)</Label>
        <Input
          id="custom-data-label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          disabled={isInspecting}
          placeholder={isInspecting ? 'Reading capabilities…' : undefined}
          data-testid="custom-data-label-input"
        />
      </div>

      <div className="flex items-center gap-2">
        <Switch
          id="custom-data-visible-to-customer"
          checked={visibleToCustomer}
          onCheckedChange={setVisibleToCustomer}
          data-testid="custom-data-visible-to-customer-toggle"
        />
        <Label htmlFor="custom-data-visible-to-customer" className="text-sm">
          Make visible to customer
        </Label>
      </div>

      {error && (
        <Text className="text-sm text-red-50" data-testid="custom-data-form-error">
          {error}
        </Text>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={close} data-testid="custom-data-form-cancel">
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={addMutation.isPending}
          disabled={!capabilitiesUrl.trim()}
          data-testid="custom-data-form-submit"
        >
          Add layer
        </Button>
      </div>
    </form>
  );
}
